<template>
  <TransitionRoot as="template" :show="isInvitationModalOpen">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="$emit('close')">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="fixed inset-0 dark:bg-true-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
        </TransitionChild>
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div class="inline-block align-bottom dark:bg-true-gray-800 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <!-- Close Button -->
            <div class="block absolute top-0 right-0 pt-4 pr-4">
              <button type="button" class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" @click="$emit('close')" :disabled="isSubmitting">
                <span class="sr-only">Close</span>
                <XIcon class="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div class="relative px-4 sm:px-6 lg:px-8 max-w-xl mx-auto py-4 mt-4">
              <div class="dark:text-gray-400 text-gray-600 leading-6 text-md max-w-prose mx-auto text-center">
                <span class="block text-base dark:text-yellow-300 text-yellow-500 font-semibold tracking-wide uppercase">Private Access</span>
                <h1 class="text-2xl tracking-tight font-extrabold dark:text-gray-300 text-gray-700 md:text-3xl my-6">
                  You've Been Invited
                </h1>

                <p class="my-4">
                  As an existing Skilltech user, you're invited to privately access the new Skilltech platform before its public release.
                </p>
                <p class="my-4">
                  Discover a new career platform built to help aviation professionals prepare for applications, interviews, resumes, and employment opportunities.
                </p>
                

                <div class="my-6">
                  <div class="w-full overflow-hidden rounded-lg shadow-sm">
                    <img
                      src="@/assets/images/skilltech-platform-preview.png"
                      alt="Preview of the new Skilltech platform"
                      class="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                

                <div class="my-6">
                  <!-- <p class="dark:text-gray-300 text-gray-700 mb-2">Invitation expires in:</p> -->
                  <p class="my-4">
                  Access is limited and this invitation will expire soon.
                </p>
                  <div class="flex items-center justify-center space-x-2">
                    <div class="flex flex-col items-center dark:bg-true-gray-900 bg-gray-100 rounded-lg px-4 py-2 min-w-[4.5rem]">
                      <span class="font-bold text-3xl dark:text-white text-gray-900 tabular-nums">{{ pad(countdown.hours) }}</span>
                      <span class="text-xs uppercase tracking-wide dark:text-gray-500 text-gray-400">Hrs</span>
                    </div>
                    <span class="font-bold text-2xl dark:text-gray-500 text-gray-400">:</span>
                    <div class="flex flex-col items-center dark:bg-true-gray-900 bg-gray-100 rounded-lg px-4 py-2 min-w-[4.5rem]">
                      <span class="font-bold text-3xl dark:text-white text-gray-900 tabular-nums">{{ pad(countdown.minutes) }}</span>
                      <span class="text-xs uppercase tracking-wide dark:text-gray-500 text-gray-400">Min</span>
                    </div>
                    <span class="font-bold text-2xl dark:text-gray-500 text-gray-400">:</span>
                    <div class="flex flex-col items-center dark:bg-true-gray-900 bg-gray-100 rounded-lg px-4 py-2 min-w-[4.5rem]">
                      <span class="font-bold text-3xl dark:text-white text-gray-900 tabular-nums">{{ pad(countdown.seconds) }}</span>
                      <span class="text-xs uppercase tracking-wide dark:text-gray-500 text-gray-400">Sec</span>
                    </div>
                  </div>
                </div>

                <button
                  @click="$emit('accept')"
                  :disabled="isSubmitting"
                  class="mt-2 text-white bg-indigo-600 w-full py-4 border border-transparent rounded-md shadow-sm px-4 inline-flex justify-center text-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto"
                >
                  Accept Invitation
                </button>

                <button
                  @click="$emit('decline')"
                  :disabled="isSubmitting"
                  class="mt-3 dark:text-gray-400 text-gray-500 w-full py-3 border border-transparent rounded-md px-4 inline-flex justify-center text-sm font-medium hover:underline focus:outline-none disabled:opacity-30 disabled:pointer-events-none disabled:cursor-auto"
                >
                  I decline this invitation
                </button>

                <p class="mt-2 text-xs dark:text-gray-500 text-gray-400">
                  Declining will permanently remove this invitation from your account.
                </p>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { XIcon } from "@heroicons/vue/solid";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogOverlay,
} from "@headlessui/vue";
import { invitationService } from "@/utils/invitation";

export default {
  components: { XIcon, TransitionRoot, TransitionChild, Dialog, DialogOverlay },
  props: {
    isInvitationModalOpen: { type: Boolean, default: false },
    expiresAt: { type: Number, default: null },
    isSubmitting: { type: Boolean, default: false },
  },
  emits: ["close", "accept", "decline"],
  data() {
    return {
      countdown: { hours: 0, minutes: 0, seconds: 0, expired: false },
      tickInterval: null,
    };
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    tick() {
      this.countdown = invitationService.getCountdownParts(this.expiresAt);
      if (this.countdown.expired) this.stopTicking();
    },
    stopTicking() {
      if (this.tickInterval) {
        clearInterval(this.tickInterval);
        this.tickInterval = null;
      }
    },
  },
  watch: {
    isInvitationModalOpen(isOpen) {
      if (isOpen) {
        this.tick();
        if (!this.tickInterval) this.tickInterval = setInterval(this.tick, 1000);
      } else {
        this.stopTicking();
      }
    },
  },
  beforeUnmount() {
    this.stopTicking();
  },
};
</script>
